import { useCallback } from 'react';

import { useDeviceType } from '@braskai/ui-kit';

import { PlansByCategory } from '@/widgets/pricing/pricingCards/lib/types';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, PlanCategory, Price } from '@/shared/types/Billing';

import { CardHeader } from './components/CardHeader';
import { DiscountChip } from './components/DiscountChip';
import { PriceBlock } from './components/PriceBlock';
import { PricingCardFeatures } from './components/PricingCardFeatures/PricingCardFeatures';
import { useChipLabel } from './hooks/useChipLabel';
import { usePricingCardState } from './hooks/usePricingCardState';
import { SContainer } from './styled';

type PricingCardProps = {
  chosenPlanId: null | string;
  plansByCategory: PlansByCategory;
  selectedPaymentPeriod: PaymentInterval;
  onActionButtonClick: (priceId: Price['id'], plan: Plan) => void;
};

export const PricingCard = ({
  chosenPlanId,
  plansByCategory,
  selectedPaymentPeriod,
  onActionButtonClick,
}: PricingCardProps) => {
  const { isMobile } = useDeviceType();
  const { plans } = plansByCategory;

  const {
    activePlan,
    bundles,
    currentPeriodPrice,
    isButtonHidden,
    isCurrentPlan,
    isDisabled,
    isInitiallyActiveSubscription,
    isSliderVisible,
    minBundleIndex,
    selectedBundlePosition,
    setActivePlan,
  } = usePricingCardState({ plansByCategory, selectedPaymentPeriod });

  const chipLabel = useChipLabel({
    plan: activePlan,
    selectedPaymentPeriod,
  });

  const handleBundleChange = useCallback(
    (bundlePosition: number) => {
      const newPlan = plans.find((plan) => plan.metadata.bundle_position === bundlePosition);
      if (!newPlan) return;

      if (newPlan.category === PlanCategory.CreatorPro) {
        MixpanelEvents.bundleSwitchCreatorPro();
      }
      if (newPlan.category === PlanCategory.Business) {
        MixpanelEvents.bundleSwitchBusiness();
      }

      setActivePlan(newPlan);
    },
    [plans, setActivePlan],
  );

  const isEnterprise = activePlan.category === PlanCategory.Enterprise;
  const isCreatorPro = activePlan.category === PlanCategory.CreatorPro;
  const isBusiness = activePlan.category === PlanCategory.Business;



  return (
    <SContainer
      $isBusiness={isBusiness}
      $isCreatorPro={isCreatorPro}
      $isCurrentPlan={isCurrentPlan}
      $isDisabled={isDisabled}
      $isEnterprise={isEnterprise}
    >
      <CardHeader isPriceActive={isCurrentPlan} plan={activePlan} />

      <PriceBlock
        bundles={bundles}
        chosenPlanId={chosenPlanId}
        isActiveSubscription={isInitiallyActiveSubscription}
        isButtonHidden={isButtonHidden}
        isDisabled={isDisabled}
        isPriceActive={isCurrentPlan}
        isSliderVisible={isSliderVisible}
        minIndexInBundle={minBundleIndex}
        plan={activePlan}
        planPrice={currentPeriodPrice}
        selectedBundlePlanPosition={selectedBundlePosition}
        onActionButtonClick={onActionButtonClick}
        onChangeBundle={handleBundleChange}
      />

      {!isMobile && (
        <PricingCardFeatures isEnterprise={isEnterprise} pricingCardActivePlan={activePlan} />
      )}

      {chipLabel && <DiscountChip label={chipLabel} />}
    </SContainer>
  );
};
